import { Paper, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import type { PartnerMetricsRow } from "../metrics.types";
import { projectMetricsInsight } from "../metricsInsight";
import { usePartnerActivity } from "./usePartnerActivity";
import { MetricsInsightGroups } from "../components/KpiTileWall";
import { PartnerActivityFilterBar } from "./components/PartnerActivityFilterBar";
import { PartnerActivityTable } from "./components/PartnerActivityTable";
import { PartnerActivityTiles } from "./components/PartnerActivityTiles";
import { PartnerActivitySortMenu } from "./components/PartnerActivitySortMenu";
import { PartnerTrendChart } from "./components/PartnerTrendChart";

interface PartnerDeepDiveProps {
  row: PartnerMetricsRow;
}

/**
 * The per-partner deep dive: the partner's Metrics insight tiles, 30-day trend
 * chart and Recent Activity. Shown for the dashboard's selected partner; see
 * docs/architecture/context.md "Per-partner deep dive".
 */
export function PartnerDeepDive({ row }: PartnerDeepDiveProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const activity = usePartnerActivity({
    partnerId: row.id,
    entries: row.partner.usage.recentActivity,
  });

  const insight = projectMetricsInsight({ kind: "partner", row });

  return (
    <Stack
      spacing={2}
      component="section"
      role="region"
      aria-label={`${row.partner.name} deep dive`}
    >
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {row.partner.name} — Deep Dive
        </Typography>
        <MetricsInsightGroups groups={insight.groups} />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <PartnerTrendChart trend={row.partner.usage.volumeTrend} />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Recent Activity</Typography>
          {isMobile && (
            <PartnerActivitySortMenu sort={activity.sort.value} onChange={activity.sort.set} />
          )}
        </Stack>
        <PartnerActivityFilterBar
          filters={activity.filters.value}
          isDefault={activity.filters.isDefault}
          shownCount={activity.entries.length}
          totalCount={activity.totalCount}
          onChange={activity.filters.set}
          onReset={activity.filters.reset}
        />
        {activity.averageResults !== null && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Avg. results per search/query shown below: {activity.averageResults.toLocaleString()}
          </Typography>
        )}
        {isMobile ? (
          <PartnerActivityTiles entries={activity.entries} />
        ) : (
          <PartnerActivityTable
            entries={activity.entries}
            sort={activity.sort.value}
            onSort={activity.sort.toggleColumn}
          />
        )}
      </Paper>
    </Stack>
  );
}
